import { SdkContext } from '../internal'

/** F-218: a private note of the player, pinned to a second of a lesson or an episode (or to none). */
export interface PlayerNote {
  id: string
  courseId: string | null
  lessonId: string | null
  episodeId: string | null
  /** `null`: a note without a minute (a lesson without a video). */
  positionSeconds: number | null
  text: string
  createdAt: string
  updatedAt: string
}

export interface PlayerNoteInput {
  text: string
  positionSeconds?: number | null
}

/** "Minhas anotações" of a course: the caller's notes grouped by lesson, in the course order. */
export interface CourseNotes {
  courseId: string
  totalNotes: number
  lessons: Array<{
    lesson: { id: string; title: string; type: string; order: number }
    module: { id: string; title: string; order: number }
    notes: PlayerNote[]
  }>
}

/** What a list answer may look like: the array, or a page of it. */
const asList = (result: PlayerNote[] | { items?: PlayerNote[] } | null | undefined): PlayerNote[] =>
  Array.isArray(result) ? result : result?.items || []

/**
 * F-218: the caller's own notes. Only the author reaches a note (404 for anyone else's);
 * the refusals are 403 `playerNotes.mustBeEnrolled` and 409 `playerNotes.limitReached`.
 */
export class PlayerNotesResource {
  constructor(private ctx: SdkContext) {}

  async listLesson(lessonId: string): Promise<PlayerNote[]> {
    return asList(await this.ctx.call<PlayerNote[]>({ httpMethod: 'GET', path: '/courses/lesson/{lessonId}/notes' }, { lessonId }))
  }

  async createLesson(lessonId: string, body: PlayerNoteInput): Promise<PlayerNote> {
    return this.ctx.call<PlayerNote>({ httpMethod: 'POST', path: '/courses/lesson/{lessonId}/notes', hasBody: true }, { lessonId, body })
  }

  async listCourse(courseId: string): Promise<CourseNotes> {
    return this.ctx.call<CourseNotes>({ httpMethod: 'GET', path: '/courses/{courseId}/notes' }, { courseId })
  }

  async listEpisode(episodeId: string): Promise<PlayerNote[]> {
    return asList(await this.ctx.call<PlayerNote[]>({ httpMethod: 'GET', path: '/media-episodes/{id}/notes' }, { id: episodeId }))
  }

  async createEpisode(episodeId: string, body: PlayerNoteInput): Promise<PlayerNote> {
    return this.ctx.call<PlayerNote>({ httpMethod: 'POST', path: '/media-episodes/{id}/notes', hasBody: true }, { id: episodeId, body })
  }

  async update(noteId: string, body: Partial<PlayerNoteInput>): Promise<PlayerNote> {
    return this.ctx.call<PlayerNote>({ httpMethod: 'PUT', path: '/player-notes/{noteId}', hasBody: true }, { noteId, body })
  }

  async delete(noteId: string): Promise<{ id: string }> {
    return this.ctx.call<{ id: string }>({ httpMethod: 'DELETE', path: '/player-notes/{noteId}' }, { noteId })
  }
}
